/**
 * Plan-generation event bus — the surface-agnostic fan-out for "a fuel-plan
 * build started / finished". The app-root PlanGenerationProvider is the only
 * publisher; adapters subscribe:
 *
 *   - ToastAdapter lives inside PlanGenerationProvider itself (needs React
 *     context for ToastProvider + navigation).
 *   - Other same-origin tabs receive events over BroadcastChannel — including,
 *     later, a Shopify theme snippet on storefront pages served from the same
 *     origin as the app proxy. That listener is plain JS, which is why this
 *     module is deliberately not React.
 *
 * Mirrors the app's other cross-tree signals (`notifyPreferencesSaved`,
 * `FUELING_PROFILE_CHANGED_EVENT`, the planRegenerate registry).
 *
 * The event shape is versioned (`v: 1`) and designed to deserialize from a
 * future server-side notification inbox row (projected off the
 * `fuel_plan.completed` domain event) — don't add fields the server couldn't
 * populate.
 */

export type PlanGenerationSource =
  | "regenerate"
  | "box"
  | "race_day_kit"
  | "milestone_kit"
  | "generate"
  | "unknown";

export type PlanGenerationMeta = {
  source?: PlanGenerationSource;
  /** For regenerates: the plan the build replaces (drives return-navigation). */
  sourcePlanId?: string;
  journeyId?: string;
  dropIndex?: number;
  /** Human label for toasts — "Fuel box 2", "Race-day kit"… */
  label?: string;
};

export type PlanGenerationEvent = {
  v: 1;
  /** `started` fires at track-time; the rest map 1:1 to terminal results. */
  type: "started" | "completed" | "superseded" | "failed" | "expired" | "timeout" | "auth_blocked";
  requestId: string;
  /** Set on `completed` (and sometimes `superseded`). */
  fuelPlanId?: string | null;
  /** Curated server failure copy — set on `failed` when available. */
  failureSummary?: string | null;
  /**
   * `c:<customerId>` for signed-in customers, `"anon"` otherwise. Lets a
   * cross-tab listener drop events from another login on a shared device.
   */
  principalKey: string;
  /** ISO — when the build was dispatched (anchors elapsed-time progress UI). */
  startedAt?: string | null;
  /** ISO — when this event fired. */
  at: string;
  meta: PlanGenerationMeta;
};

const WINDOW_EVENT = "fg:plan-generation";
const CHANNEL_NAME = "fg:plan-generation";

let channel: BroadcastChannel | null | undefined;
function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;
  } catch {
    channel = null;
  }
  return channel;
}

/** Publish to this tab (CustomEvent) and every other same-origin tab. */
export function publishPlanGenerationEvent(event: PlanGenerationEvent): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<PlanGenerationEvent>(WINDOW_EVENT, { detail: event }));
  try {
    getChannel()?.postMessage(event);
  } catch {
    // BroadcastChannel is best-effort — same-tab listeners already got the
    // CustomEvent, and cross-device delivery is the (future) inbox's job.
  }
}

/**
 * Subscribe to plan-generation events from this tab AND other same-origin
 * tabs. Returns an unsubscribe. Listeners should filter on `principalKey`.
 */
export function subscribePlanGenerationEvents(
  listener: (event: PlanGenerationEvent) => void
): () => void {
  if (typeof window === "undefined") return () => {};
  const onWindow = (e: Event) => {
    const detail = (e as CustomEvent<PlanGenerationEvent>).detail;
    if (detail?.v === 1) listener(detail);
  };
  const onMessage = (e: MessageEvent) => {
    const data = e.data as PlanGenerationEvent | undefined;
    if (data?.v === 1) listener(data);
  };
  window.addEventListener(WINDOW_EVENT, onWindow);
  const ch = getChannel();
  ch?.addEventListener("message", onMessage);
  return () => {
    window.removeEventListener(WINDOW_EVENT, onWindow);
    ch?.removeEventListener("message", onMessage);
  };
}
